import { FC, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
} from "@/components/ui/field";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoginOption, SKUPageContentTheme } from "@/data/types";
import { SKUFormValues } from "@/lib/forms";
import { buildSelectLoginOptions } from "@/lib/loginOptions";
import { canEditSKU } from "@/lib/permissions";
import {
  FileType,
  HEADER_LOGO_MAX_SIZE,
  HEADER_LOGO_MIME_TYPES,
} from "@/lib/uploads";
import useStore from "@/state";

import { FileUploadInput } from "./file-upload-input";

type Props = {
  availableLoginOptions: LoginOption[];
};

export const LegitimateAttributes: FC<Props> = ({ availableLoginOptions }) => {
  const form = useFormContext<SKUFormValues>();
  const { session } = useStore();

  const hasPermission = useMemo(() => {
    return canEditSKU(session?.permissions);
  }, [session?.permissions]);

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="header_logo_url"
        disabled={!hasPermission}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Header Logo</FormLabel>
            <FormControl>
              <FileUploadInput
                {...field}
                fileType={FileType.IMAGE}
                maxFileSize={HEADER_LOGO_MAX_SIZE}
                accept={HEADER_LOGO_MIME_TYPES}
              />
            </FormControl>
            <FormDescription>
              Replace default Legitimate logo in the header of the digital
              experience. Only the PNG and SVG formats are supported. Max 5MB.
            </FormDescription>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="theme"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Styling</FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
              disabled={field.disabled}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select a theme" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value={SKUPageContentTheme.DARK}>
                  Dark Theme
                </SelectItem>
                <SelectItem value={SKUPageContentTheme.LIGHT}>
                  Light Theme
                </SelectItem>
                <SelectItem value={SKUPageContentTheme.SYSTEM}>
                  System
                </SelectItem>
              </SelectContent>
            </Select>
          </FormItem>
        )}
      />

      <FieldGroup>
        <Controller
          name="login_options"
          control={form.control}
          render={({ field, fieldState }) => (
            <FieldSet>
              <FieldLegend variant="label">Account Login Options</FieldLegend>
              <FieldDescription>
                Choose how end-consumers who tap a Legitimate NFC tag will
                login.
              </FieldDescription>
              <FieldGroup data-slot="checkbox-group" className="gap-3">
                {buildSelectLoginOptions(availableLoginOptions).map(
                  (loginOption) => (
                    <Field
                      key={loginOption.value}
                      orientation="horizontal"
                      data-invalid={fieldState.invalid}
                    >
                      <Checkbox
                        id={`login-option-${loginOption.value}`}
                        name={loginOption.label}
                        aria-invalid={fieldState.invalid}
                        checked={field.value.includes(loginOption.value)}
                        onCheckedChange={(checked) => {
                          const newValue = checked
                            ? [...field.value, loginOption.value]
                            : field.value.filter(
                                (value) => value !== loginOption.value,
                              );
                          field.onChange(newValue);
                        }}
                      />
                      <FieldLabel
                        htmlFor={`login-option-${loginOption.value}`}
                        className="font-normal"
                      >
                        {loginOption.label}
                      </FieldLabel>
                    </Field>
                  ),
                )}
              </FieldGroup>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </FieldSet>
          )}
        />
      </FieldGroup>

      <FieldSeparator />

      <FormField
        control={form.control}
        name="display_serial_number"
        render={({ field }) => (
          <FormItem className="flex space-x-2 mt-6">
            <FormLabel className="font-normal gap-2 items-start">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="grid gap-1.5 leading-none">
                <span className="font-medium my-0.5">Show Serial Number</span>
                <FormDescription>
                  Display each product's unique serial number on the digital
                  experience to represent rarity and exclusivity
                </FormDescription>
              </div>
            </FormLabel>
          </FormItem>
        )}
      />
    </div>
  );
};
